import type { FinancialSectionV2 } from "@credit-acceptance/step6-bff-client";

import { calculateTotalDownPaymentDeduction } from "./Financial.calculations";

export interface ItemizationAmounts {
  sellingPrice?: number;
  accessories?: number;
  totalSellingPrice?: number;
  salesTax?: number;
  isSalesTaxManual: boolean;
  cashDown?: number;
  grossTradeIn?: number;
  /** Always non-positive: the payoff subtracts from the down payment group. */
  tradeInPayoff?: number;
  /** Negative deduction, undefined when the response has no down payment figures. */
  totalDownPayment?: number;
  vsc?: number;
  gap?: number;
  otherBackendProducts?: number;
  totalCustomerFees?: number;
  amountFinanced?: number;
}

/**
 * Flattens the Itemization of Amount Financed figures out of the v2 financial section.
 * Every figure stays `undefined` when the response omits it so the UI can show a dash.
 * @param financial - The `financial` section of the v2 dashboard response.
 * @returns {ItemizationAmounts} The itemization amounts, in display sign convention.
 */
export function mapItemization(financial?: FinancialSectionV2): ItemizationAmounts {
  const itemization = financial?.itemization;
  const downPayment = itemization?.downPayment;
  const payoff = downPayment?.tradeInPayoff;
  const tradeInPayoff = payoff === undefined ? undefined : -Math.abs(payoff);

  const hasDownPayment =
    downPayment?.cashDown !== undefined || downPayment?.grossTradeIn !== undefined || payoff !== undefined;

  return {
    sellingPrice: itemization?.sellingPrice,
    accessories: financial?.accessories?.totalPrice,
    totalSellingPrice: itemization?.totalSellingPrice,
    salesTax: itemization?.salesTax?.total,
    isSalesTaxManual: itemization?.salesTax?.isManual === true,
    cashDown: downPayment?.cashDown,
    grossTradeIn: downPayment?.grossTradeIn,
    tradeInPayoff,
    totalDownPayment: hasDownPayment
      ? calculateTotalDownPaymentDeduction([
          { amount: downPayment?.cashDown ?? 0 },
          { amount: downPayment?.grossTradeIn ?? 0 },
          { amount: tradeInPayoff ?? 0 },
        ])
      : undefined,
    vsc: itemization?.vsc,
    gap: itemization?.gap,
    otherBackendProducts: itemization?.obpSellingPrice,
    totalCustomerFees: financial?.totalCustomerFees,
    amountFinanced: itemization?.amountFinanced,
  };
}
